import type { SetRequired } from 'type-fest';

import type {
  CastaiUsageV1beta1ResourceUsage as ClusterMetricsAPIResponse,
  ExternalclusterV1Cluster as ClusterAPIResponse,
  ExternalclusterV1AnywhereClusterParams as AnywhereParams,
} from '@cast/openapi';
import { Cloud, K8sProvider } from '@cast/shared-types';

export type {
  ExternalclusterV1ListNodesResponse as NodeListResponse,
  ExternalclusterV1ListClustersResponse as ClusterListResponse,
  ExternalclusterV1OnboardingPath as ClusterOnboardingPath,
} from '@cast/openapi';
export { ExternalclusterV1OnboardingPath as ClusterOnboardingPathEnum } from '@cast/openapi';

export enum ClusterStatus {
  CONNECTING = 'connecting',
  READ_ONLY = 'read-only',
  READY = 'ready',
  HIBERNATED = 'hibernated',
  HIBERNATING = 'hibernating',
  RESUMING = 'resuming',
  DELETED = 'deleted',
  DELETING = 'deleting',
  WARNING = 'warning',
  FAILED = 'failed',
  DISCOVERED = 'discovered', // from tag cloud-discovery=true
  NOT_FOUND = 'not-found', // display-only; derived from failed reconciliation
  ARCHIVED = 'archived',
}

export enum AgentStatus {
  CONNECTING = 'waiting-connection',
  ONLINE = 'online',
  NOT_RESPONDING = 'non-responding',
  DISCONNECTING = 'disconnecting',
  DISCONNECTED = 'disconnected',
}

export enum ClusterDisplayStatus {
  CONNECTING = ClusterStatus.CONNECTING,
  READ_ONLY = ClusterStatus.READ_ONLY,
  READY = ClusterStatus.READY,
  HIBERNATED = ClusterStatus.HIBERNATED,
  HIBERNATING = ClusterStatus.HIBERNATING,
  RESUMING = ClusterStatus.RESUMING,
  DELETED = ClusterStatus.DELETED,
  DELETING = ClusterStatus.DELETING,
  WARNING = ClusterStatus.WARNING,
  FAILED = ClusterStatus.FAILED,
  DISCOVERED = ClusterStatus.DISCOVERED,
  NOT_FOUND = ClusterStatus.NOT_FOUND,
  ARCHIVED = ClusterStatus.ARCHIVED,
  WAITING_CONNECTION = AgentStatus.CONNECTING,
  ONLINE = AgentStatus.ONLINE,
  NOT_RESPONDING = AgentStatus.NOT_RESPONDING,
  DISCONNECTING = AgentStatus.DISCONNECTING,
  DISCONNECTED = AgentStatus.DISCONNECTED,
}

export type ClusterRegion = {
  name: string;
  displayName: string;
};

export type CloudDiscoveryTags = Partial<{
  'cloud-discovery': 'true' | 'false';
  'cloud-discovery-cpu': string;
  'cloud-discovery-cpu-hourly-cost-usd': string;
  'cloud-discovery-memory-mib': string;
  'cloud-discovery-on-demand-node-count': string;
  'cloud-discovery-source-aws-account-id': string;
  'cloud-discovery-source-integration-id': string;
  'cloud-discovery-spot-node-count': string;
  'cloud-discovery-total-monthly-cost-usd': string;
}>;

// Transform API response type to comply with reality
type ClusterResponse = Omit<
  SetRequired<ClusterAPIResponse, 'id' | 'name'>,
  'tags'
> & {
  providerType: K8sProvider;
  agentStatus: AgentStatus;
  status: ClusterStatus;
  reconcileError?: string;
  region: ClusterRegion;
  metrics?: ClusterMetricsAPIResponse;
  tags?: Record<string, string> & CloudDiscoveryTags;
};

export type Cluster = ClusterResponse & {
  displayStatus: ClusterDisplayStatus;
};

export type { ClusterResponse, ClusterAPIResponse };

export type {
  ExternalclusterV1ClusterUpdate as ClusterEditParams,
  ExternalclusterV1ClusterReconcileInfo as ClusterReconcileInfo,
  ExternalclusterV1DisconnectConfig as ClusterDisconnectParams,
  ExternalclusterV1GetCleanupScriptResponse as ClusterCleanupResponse,
  CastaiUsageV1beta1ClusterMetadata as ClusterMetadata,
  ExternalclusterV1Zone as ClusterZone,
} from '@cast/openapi';

export { ExternalclusterV1ClusterReconcileInfoReconcileStatus as ClusterReconcileStatus } from '@cast/openapi';

type ClusterBase = {
  id: string;
  name: string;
  organizationId: string;
  credentialsId: string;
  createdAt: string;
  region: ClusterRegion;
  status: ClusterStatus;
  agentStatus: AgentStatus;
  agentSnapshotReceivedAt: string;
  providerType: K8sProvider;
  providerNamespaceId?: string;
  displayStatus: ClusterDisplayStatus;
  reconcileError?: string;
};

type EKSClusterDetails = {
  clusterName: string;
  region: string;
  accountId: string;
  securityGroups: string[];
};

export type EKSCluster = ClusterBase & {
  eks: EKSClusterDetails;
};

type GKEClusterDetails = {
  clusterName: string;
  region: string;
  projectId: string;
};

export type GKECluster = ClusterBase & {
  gke: GKEClusterDetails;
};

type AKSClusterDetails = {
  nodeResourceGroup: string;
  region: string;
  subscriptionId: string;
};

export type AKSCluster = ClusterBase & {
  aks: AKSClusterDetails;
};

type KOPSClusterDetails = {
  clusterName: string;
  region: string;
  cloud: Cloud;
  stateStore: string;
};

export type KopsCluster = ClusterBase & {
  kops: KOPSClusterDetails;
};

type ROSAClusterDetails = {
  cloud: Cloud;
  clusterName: string;
  internalId: string;
  region: string;
};

export type RosaCluster = ClusterBase & {
  openshift: ROSAClusterDetails;
};

export type AnywhereCluster = ClusterBase & {
  anywhere: AnywhereParams;
};
